import React, { useEffect, useState } from "react";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function StockView() {
  const [latestStock, setLatestStock] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [filteredStock, setFilteredStock] = useState([]);
  const [selectedSearchField, setSelectedSearchField] = useState("product_name");

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const userRole = userResponse.data.role;

        if (userRole === "Main Admin") {
          fetchAllLocations();
        } else {
          fetchFilteredLocations();
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
        toast.error("Error fetching user role.");
      }
    };

    fetchUserRole();
  }, []);

  const fetchAllLocations = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/locations`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const formattedLocations = response.data.map((loc) => ({
        value: loc._id,
        label: loc.location_name,
      }));

      setLocations(formattedLocations);

      if (formattedLocations.length === 1) {
        setSelectedLocation(formattedLocations[0]); // Auto-select if only one location
      } else {
        setSelectedLocation({ value: "all", label: "Select All" });
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      toast.error("Error fetching locations.");
    }
  };

  const fetchFilteredLocations = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/products/filtered-locations`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      let formattedLocations = response.data.locations.map((loc) => ({
        value: loc._id,
        label: loc.location_name,
      }));

      formattedLocations = [{ value: "all", label: "Select All" }, ...formattedLocations];

      setLocations(formattedLocations);

      if (formattedLocations.length === 1) {
        setSelectedLocation(formattedLocations[0]); // Auto-select if there is only one location
      } else {
        setSelectedLocation({ value: "all", label: "Select All" });
      }
    } catch (error) {
      console.error("Error fetching filtered locations:", error);
      toast.error("Error fetching filtered locations.");
    }
  };

  useEffect(() => {
    const fetchStock = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        let url = `${API_BASE_URL}/latestStock/getStockByLocations`;

        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const latestStock = response.data || [];

        const formattedLatestStock = latestStock.map((stock) => ({
          id: stock.product_id,
          product_name: stock.product_name || "Unknown",
          product_code: stock.product_code || "N/A",
          updated_stock_qty: stock.updated_stock_qty || 0,
          latest_price: stock.latest_price?.$numberDecimal || "0.00",
          product_image: stock.product_image || "",
          location: stock.location,
          unit: stock.unit,
        }));

        setLatestStock(formattedLatestStock);
      } catch (error) {
        console.error("Error fetching latest stock:", error);
        toast.error("Error fetching latest stock.");
      }
    };

    fetchStock();
  }, []);

  useEffect(() => {
    let filtered = [...latestStock];

    if (selectedLocation && selectedLocation.value !== "all") {
      filtered = filtered.filter(
        (product) =>
          product.location.toLowerCase().trim() ===
          selectedLocation.label.toLowerCase().trim()
      );
    }

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((product) => {
        const fieldValue = product[selectedSearchField];
        return fieldValue && fieldValue.toString().toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    if (selectedLocation && selectedLocation.value === "all") {
      filtered = filtered.map((product) => ({
        ...product,
        id: `${product.product_code}-${product.location}`,
      }));
    }

    setFilteredStock(filtered);
  }, [selectedLocation, latestStock, searchTerm, selectedSearchField]);


  const handleDownloadExcel = async () => {
    if (filteredStock.length === 0) {
      toast.warn("No stock data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Current Stock");

    // Define header row for stock
    const headerRow = [
      "Product Code",
      "Product Name",
      "Location",
      "Stock Quantity",
      "Units",
      "Latest Price (LKR)"
    ];
    worksheet.addRow(headerRow);

    // Style header row
    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" }, // Light yellow
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Add data rows
    filteredStock.forEach((stock) => {
      worksheet.addRow([
        stock.product_code,
        stock.product_name,
        stock.location,
        stock.updated_stock_qty,
        stock.unit,
        Number(stock.latest_price).toFixed(2),
      ]);
    });

    // Adjust column widths based on header length or content length
    worksheet.columns.forEach((column, i) => {
      let maxLength = headerRow[i].length;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellLength = cell.value ? cell.value.toString().length : 0;
        if (cellLength > maxLength) maxLength = cellLength;
      });
      column.width = maxLength < 20 ? 20 : maxLength;
    });

    // Generate buffer and save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "current_stock.xlsx");
  };

  const columns = [
    { field: "index", 
      headerName: "#", 
      sortable: false,
      renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.row.id) + 1,},
    { field: "product_code", headerName: "Product Code", flex: 1 },
    {
      field: "product_image",
      headerName: "Image",
      flex: 1,
      renderCell: (params) =>
        params.row.product_image ? (
          <img
            src={`${process.env.REACT_APP_IMAGE_BASE_URL}${params.row.product_image}`}
            alt="Product"
            className="h-16 w-16 object-cover rounded-md"
          />
        ) : (
          "No Image"
        ),
    },
    { field: "product_name", headerName: "Product Name", flex: 1 },
    { field: "location", headerName: "Location", flex: 1 },
    { field: "updated_stock_qty", headerName: "Stock Quantity", flex: 1, headerAlign: "right", align: "right" },
    { field: "unit", headerName: "Units", flex: 1, headerAlign: "center", align: "center" },
    {
      field: "latest_price",
      headerName: "Latest Price (LKR)",
      flex: 1,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}`
    },
  ];

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">Current Available Stock</h1>
        </div>
        <div className="bg-white p-4 rounded-lg shadow mb-8">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="product_name">Product Name</option>
                <option value="product_code">Product Code</option>
                <option value="location">Location</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Products"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>         
          <DataGrid key={filteredStock.length} rows={filteredStock} columns={columns} />
        </div>
      </div>
    </div>
  );
}

export default StockView;
